import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { salesforceAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Settings, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPanel() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    instance_url: '',
    username: '',
    client_id: '',
    client_secret: '',
    is_sandbox: false,
  });

  const { data: config, isLoading } = useQuery({
    queryKey: ['salesforce-config'],
    queryFn: () => salesforceAPI.getConfig().then((res) => res.data),
  });

  React.useEffect(() => {
    if (config) {
      setFormData({
        instance_url: config.instance_url || '',
        username: config.username || '',
        client_id: config.client_id || '',
        client_secret: config.client_secret || '',
        is_sandbox: config.is_sandbox || false,
      });
    }
  }, [config]);

  const saveMutation = useMutation({
    mutationFn: (data) => salesforceAPI.createConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['salesforce-config']);
      toast.success('Salesforce configuration saved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    },
  });

  const testMutation = useMutation({
    mutationFn: () => salesforceAPI.testConnection(),
    onSuccess: () => {
      queryClient.invalidateQueries(['salesforce-config']);
      toast.success('Connection test successful!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const handleTestConnection = () => {
    testMutation.mutate();
  };

  return (
    <div className="p-8 space-y-6" data-testid="admin-panel">
      <div>
        <h1 className="text-4xl font-bold flex items-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Settings className="w-10 h-10 mr-3 text-[#09409C]" />
          <span className="text-gradient-primary">Admin Panel</span>
        </h1>
        <p className="text-muted-foreground mt-2 text-lg">Configure Salesforce integration and system settings</p>
      </div>

      <Card className="max-w-3xl" data-testid="salesforce-config-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Salesforce Connection
              </CardTitle>
              <CardDescription className="mt-2">
                Configure your Salesforce credentials to enable real-time synchronization
              </CardDescription>
            </div>
            {config && (
              <Badge
                variant="outline"
                className={config.is_connected ? 'border-green-500 text-green-700' : 'border-gray-300 text-gray-700'}
              >
                {config.is_connected ? (
                  <>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Connected
                  </>
                ) : (
                  <>
                    <XCircle className="w-3 h-3 mr-1" />
                    Not Connected
                  </>
                )}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-[#09409C]" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="salesforce-config-form">
              <div className="space-y-2">
                <Label htmlFor="instance_url">Instance URL</Label>
                <Input
                  id="instance_url"
                  placeholder="https://your-instance.salesforce.com"
                  value={formData.instance_url}
                  onChange={(e) => setFormData({ ...formData, instance_url: e.target.value })}
                  required
                  data-testid="sf-instance-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="user@constellation.com"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  data-testid="sf-username-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_id">Client ID (Consumer Key)</Label>
                <Input
                  id="client_id"
                  placeholder="Your Connected App Client ID"
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  required
                  data-testid="sf-client-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_secret">Client Secret (Consumer Secret)</Label>
                <Input
                  id="client_secret"
                  type="password"
                  placeholder="Your Connected App Client Secret"
                  value={formData.client_secret}
                  onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                  required
                  data-testid="sf-client-secret-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_sandbox"
                  checked={formData.is_sandbox}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_sandbox: checked })}
                  data-testid="sf-sandbox-switch"
                />
                <Label htmlFor="is_sandbox" className="cursor-pointer">
                  Sandbox Environment
                </Label>
              </div>

              {config?.last_sync && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Last sync: {new Date(config.last_sync).toLocaleString()}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-[#09409C] to-[#146CBB] hover:from-[#072e70] hover:to-[#105a99]"
                  disabled={saveMutation.isPending}
                  data-testid="save-sf-config-button"
                >
                  {saveMutation.isPending ? 'Saving...' : 'Save Configuration'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testMutation.isPending || !config}
                  data-testid="test-sf-connection-button"
                >
                  {testMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            About Salesforce Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>
            The Salesforce integration uses a Connected App to securely connect to your Salesforce instance. This
            enables real-time synchronization of contract data, accounts, and service locations.
          </p>
          <div className="space-y-2">
            <p className="font-semibold text-foreground">How to set up:</p>
            <ol className="list-decimal list-inside space-y-1 pl-4">
              <li>Create a Connected App in your Salesforce org</li>
              <li>Enable OAuth settings with appropriate scopes (api, refresh_token)</li>
              <li>Copy the Consumer Key and Consumer Secret</li>
              <li>Enter your credentials above and save</li>
              <li>Test the connection to verify</li>
            </ol>
          </div>
          <p className="italic">
            Note: This is a mock implementation for Phase 1. In production, this would establish a real connection
            using OAuth 2.0 JWT Bearer flow.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
